import { NgxPermissionsService } from 'ngx-permissions';
import {
  catchError,
  EMPTY,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserApiClient } from '@ellf/app-api-client';
import { NgrxLoaderFacade } from '@ellf/app-ngrx-loader';
import { ToastService } from '@ellf/app-ui-kit';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import { AuthService } from '../auth.service';
import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private userApiClient: UserApiClient,
    private permissionsService: NgxPermissionsService,
    private authService: AuthService,
    private authFacade: AuthFacade,
    private router: Router,
    private loader: NgrxLoaderFacade,
    private toastService: ToastService,
  ) {}

  failure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          // AuthActions.loginFailure,
          // AuthActions.forgotPasswordFailure,
          AuthActions.resetPasswordFailure,
        ),
        tap((a) => this.toastService.errorFeedback(a.error)),
      ),
    { dispatch: false },
  );

  loginAAD$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithAAD),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.loginAAD().pipe(
          switchMap((result) => {
            if (result.isAuthenticated) {
              return this.authService.switchToken(
                result.accessToken,
                a.rememberMe,
              );
            } else {
              this.loader.hideLoader(a);
              return EMPTY;
            }
          }),
          map((result) => {
            this.loader.hideLoader(a);
            return AuthActions.loginWithAADSuccess({ result });
          }),
          catchError((error) => {
            // eslint-disable-next-line no-console
            console.error('loginAAD error', error);
            this.loader.hideLoader(a);
            return of(
              AuthActions.loginWithAADFailure({
                error: error.error?.message || error.message,
              }),
            );
          }),
        );
      }),
    ),
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap((a) => {
        this.loader.showLoader(a);
        // Your custom service 'load' logic goes here. For now just return a success action...
        return this.authService
          .login(a.username, a.password, a.rememberMe, a.newPassword)
          .pipe(
            switchMap((result) => {
              this.loader.hideLoader(a);
              return of(AuthActions.loginSuccess({ result }));
            }),
            catchError((error: HttpErrorResponse) => {
              this.loader.hideLoader(a);
              if (error.status === 417) {
                this.authFacade.loginFormInitializeWithTmpPassword(
                  a.username,
                  a.password,
                );
                this.router.navigateByUrl('auth/login');

                return of(AuthActions.setUserMustResetPassword());
              }

              return of(
                AuthActions.loginFailure({
                  error: error.error?.message || error.message,
                }),
              );
            }),
          );
      }),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.loginWithAADSuccess),
        tap((a) => {
          // this.authService.logoutAAD();
          const url = this.authService.popTmpUrl() || '/';
          this.router.navigateByUrl(url);
        }),
      ),
    { dispatch: false },
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.logout().pipe(
          map(() => {
            this.loader.hideLoader(a);
            return AuthActions.logoutSuccess();
          }),
          catchError((err) => {
            this.loader.hideLoader(a);
            return of(AuthActions.logoutFailure(err));
          }),
        );
      }),
    ),
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap((res) => {
          // this.authService.setLocalAccessToken(res.access_token);
          this.router.navigateByUrl('/auth/login');
        }),
      ),
    { dispatch: false },
  );

  sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(() => {
          this.toastService.errorFeedback(
            'La sessione è scaduta, sarai rediretto alla pagina di login.',
          );
          // this.authService.logoutLocal();
          setTimeout(() => {
            this.authFacade.logout();
            this.loader.forceHideLoader();
          }, 3000);
        }),
      ),
    { dispatch: false },
  );

  refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forceRefresh),
      mergeMap(() => {
        return from(this.authService.refreshTokens()).pipe(
          map((response) =>
            AuthActions.forceRefreshSucces({ result: response }),
          ),
          catchError((err) => of(AuthActions.forceRefreshFail({ error: err }))),
        );
      }),
    ),
  );

  getLoggedUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getLoggedUserSuccess),
        tap(() => {
          // console.log('Utente loggato recuperato con successo');
        }),
      ),
    { dispatch: false },
  );

  getLoggedUserFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getLoggedUserFail),
        tap(() => {
          this.router.navigateByUrl('/forbidden');
        }),
      ),
    { dispatch: false },
  );

  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkAADAuth),
      mergeMap(() => {
        return from(this.authService.checkAADAuth()).pipe(
          map((response) =>
            AuthActions.checkAuthSuccess({ loggedIn: !!response }),
          ),
          catchError((err) => of(AuthActions.checkAuthFail({ error: err }))),
        );
      }),
    ),
  );

  checkAuthFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.checkAuthFail),
        tap(() => {
          this.router.navigateByUrl('login');
        }),
      ),
    { dispatch: false },
  );

  getLoggedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getLoggedUser),
      fetch({
        run: (a) => {
          // Your custom service 'load' logic goes here. For now just return a success action...
          return this.userApiClient.me().pipe(
            map((loggedUser) => {
              return AuthActions.getLoggedUserSuccess({ loggedUser });
            }),
          );
        },
        onError: (action, error) => {
          return AuthActions.getLoggedUserFail({ error: error.message });
        },
      }),
    ),
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      fetch({
        run: (a) => {
          this.loader.showLoader(a);
          return this.authService.forgotPassword(a.username).pipe(
            map((res) => {
              this.loader.hideLoader(a);
              return AuthActions.forgotPasswordSuccess();
            }),
          );
        },
        onError: (action, error) => {
          this.loader.hideLoader(action);
          return AuthActions.forgotPasswordFailure({
            error: error.message ? error.message : error,
          });
        },
      }),
    ),
  );

  forgotPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(() =>
          this.toastService.successFeedback(
            'Riceverai una email con le istruzioni per il reset della password.',
          ),
        ),
      ),
    { dispatch: false },
  );

  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      fetch({
        run: (a) => {
          this.loader.showLoader(a);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return of(a).pipe(
            switchMap((action) => {
              return this.authService
                .resetPassword(action.token, action.newPassword)
                .pipe(
                  map(() => {
                    this.loader.hideLoader(action);
                    this.router.navigateByUrl('auth/login');
                    return AuthActions.resetPasswordSuccess();
                  }),
                );
            }),
          );
        },
        onError: (action, error) => {
          this.loader.hideLoader(action);
          return AuthActions.resetPasswordFailure({
            error: error.message ? error.message : error,
          });
        },
      }),
    ),
  );
}
